// The player glossary (R1557, ui-map-interface.md §8 "The player glossary").
//
// `data/locale/<tag>/glossary.toml` holds one word and one definition per
// game term a player reads, and they reach this module as the `glossary`
// section of `gen/strings`: the key `<id>` is the word and `<id>_def` the
// definition. The language was picked when `gen/strings` evaluated, so the
// list below is built once and costs the render loop nothing.
//
// A UI sentence marks a term as `[[id]]`, which shows the glossary's own
// word, or as `[[id|words]]`, which shows the words the sentence needs
// (a plural, a case). `termsHtml` draws a mark as a term button: a small
// button with a dotted line under it, whose press opens the definition in
// the explainer card `#bartip` (the overlay acts on `data-act="term"`).
// A reader that writes plain text, a `title` for one, takes `termsText`.
//
// The module is pure: no DOM, no state. The hub's GLOSSARY card and the
// game overlay both read it.

import { LOCALE, glossary as G } from '../gen/strings';
import { esc } from './esc';

/** One term of the player glossary. */
export interface Term {
  id: string;
  /** The word as a player reads it. */
  word: string;
  /** One or two short sentences. */
  def: string;
}

/** The key suffix of a definition. */
const DEF = '_def';

/**
 * Every term of a glossary table, in the order of its word in the picked
 * language. A key with no `_def` twin is no term; the vitest guards that
 * the generated table holds none.
 */
export function termsOf(table: Readonly<Record<string, string>>): Term[] {
  return Object.keys(table)
    .filter((k) => !k.endsWith(DEF) && typeof table[k + DEF] === 'string')
    .map((id) => ({ id, word: table[id], def: table[id + DEF] }))
    .sort((a, b) => a.word.localeCompare(b.word, LOCALE));
}

/** The player glossary of the picked language. */
export const TERMS: readonly Term[] = termsOf(G as unknown as Readonly<Record<string, string>>);

const BY_ID: ReadonlyMap<string, Term> = new Map(TERMS.map((t) => [t.id, t]));

/** One term by its id, or undefined. */
export function glossaryTerm(id: string): Term | undefined {
  return BY_ID.get(id);
}

/** One run of a marked sentence: plain text, or a term and its words. */
export interface TermPiece {
  text: string;
  /** The id of a mark. Absent on a plain run. */
  term?: string;
}

/** The one token form: `[[id]]` or `[[id|words]]`. */
const MARK = /\[\[([a-z][a-z0-9_]*)(?:\|([^\]|]+))?\]\]/g;

/**
 * Splits a sentence into plain runs and marks. A mark of `[[id]]` takes
 * the word of `lookup(id)`, and a mark whose id `lookup` does not know
 * reads as plain words, so a sentence never shows a bracket.
 */
export function parseTerms(
  s: string, lookup: (id: string) => Term | undefined = glossaryTerm,
): TermPiece[] {
  const out: TermPiece[] = [];
  let at = 0;
  for (const m of s.matchAll(MARK)) {
    const i = m.index ?? 0;
    if (i > at) out.push({ text: s.slice(at, i) });
    const t = lookup(m[1]);
    const words = m[2] ?? t?.word ?? m[1];
    out.push(t ? { text: words, term: t.id } : { text: words });
    at = i + m[0].length;
  }
  if (at < s.length) out.push({ text: s.slice(at) });
  return out;
}

/** The ids a sentence marks, in order. A vitest checks each against the glossary. */
export function markedIds(s: string): string[] {
  return Array.from(s.matchAll(MARK), (m) => m[1]);
}

/** One term button: the words, a dotted line under them, and the press
 *  that opens the definition. The definition is its `title` as well, so
 *  a mouse reads it on a hover and a screen reader reads it. */
export function termButton(id: string, words: string): string {
  const t = glossaryTerm(id);
  if (!t) return esc(words);
  return `<button class="term" data-act="term" data-term="${esc(t.id)}"`
    + ` title="${esc(t.def)}">${esc(words)}</button>`;
}

/** A marked sentence as markup: the plain runs escaped, the marks as
 *  term buttons. A sentence with no mark is just escaped. */
export function termsHtml(s: string): string {
  return parseTerms(s).map((p) => (p.term ? termButton(p.term, p.text) : esc(p.text))).join('');
}

/** A marked sentence as plain text: every mark reads as its words. */
export function termsText(s: string): string {
  return parseTerms(s).map((p) => p.text).join('');
}

/** The markup of the explainer card for one term: the word, then the
 *  definition in the body ink. */
export function termCardHtml(t: Term): string {
  return `<div class="title">${esc(t.word)}</div><div class="line lead">${esc(t.def)}</div>`;
}

/**
 * The terms a filter keeps, in the glossary's order: every term whose word
 * or definition holds the query. Case and the space round the query do
 * not count. An empty query keeps every term.
 */
export function filterTerms(terms: readonly Term[], query: string): Term[] {
  const q = query.trim().toLocaleLowerCase(LOCALE);
  if (q === '') return terms.slice();
  const low = (s: string) => s.toLocaleLowerCase(LOCALE);
  return terms.filter((t) => low(t.word).includes(q) || low(t.def).includes(q));
}
